import * as React from 'react'

import type { DateValidationProps } from '../_helpers/text-field-helper'
import type { StateHookOptions, usePickerState } from '../_shared/hooks/usePickerState'
import type { PureDateInputProps } from '../_shared/PureDateInput'
import type { BasePickerProps } from '../typings/BasePicker'
import { Wrapper } from '../wrappers/Wrapper'
import type { ExtendWrapper } from '../wrappers/Wrapper'

import type { ToolbarComponentProps } from './Picker'
import { Picker } from './Picker'

export type WithKeyboardInputProps = DateValidationProps

export type WithPureInputProps = DateValidationProps & BasePickerProps & ExtendWrapper<PureDateInputProps>

export interface MakePickerOptions<T> {
  Input: any
  useState: typeof usePickerState
  useOptions: (props: any) => StateHookOptions
  getCustomProps?: (props: T) => Partial<T>
  DefaultToolbarComponent: React.ComponentType<ToolbarComponentProps>
}

export function makePickerWithState({ Input, useState, useOptions, getCustomProps, DefaultToolbarComponent }: any) {
  function PickerWithState(props: any) {
    const {
      allowKeyboardControl,
      ampm,
      animateYearScrolling,
      dateRangeIcon,
      disableFuture,
      disablePast,
      disableToolbar,
      hideTabs,
      leftArrowButtonProps,
      leftArrowIcon,
      loadingIndicator,
      maxDate,
      minDate,
      minutesStep,
      onMonthChange,
      onYearChange,
      openTo,
      orientation,
      renderDay,
      rightArrowButtonProps,
      rightArrowIcon,
      shouldDisableDate,
      strictCompareDates,
      timeIcon,
      ToolbarComponent = DefaultToolbarComponent,
      variant,
      views,
      ...other
    } = props

    const injectedProps = getCustomProps ? getCustomProps(props) : {}

    const options = useOptions(props)
    const { pickerProps, inputProps, wrapperProps } = useState(props as any, options)

    return (
      <Wrapper
        variant={variant}
        InputComponent={Input}
        DateInputProps={inputProps}
        {...injectedProps}
        {...wrapperProps}
        {...other}
      >
        <Picker
          {...pickerProps}
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          // @ts-ignore
          allowKeyboardControl={allowKeyboardControl}
          ampm={ampm}
          animateYearScrolling={animateYearScrolling}
          dateRangeIcon={dateRangeIcon}
          disableFuture={disableFuture}
          disablePast={disablePast}
          disableToolbar={disableToolbar}
          hideTabs={hideTabs}
          leftArrowButtonProps={leftArrowButtonProps}
          leftArrowIcon={leftArrowIcon}
          loadingIndicator={loadingIndicator}
          maxDate={maxDate}
          minDate={minDate}
          minutesStep={minutesStep}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
          openTo={openTo}
          orientation={orientation}
          renderDay={renderDay}
          rightArrowButtonProps={rightArrowButtonProps}
          rightArrowIcon={rightArrowIcon}
          shouldDisableDate={shouldDisableDate}
          strictCompareDates={strictCompareDates}
          timeIcon={timeIcon}
          ToolbarComponent={ToolbarComponent}
          views={views}
        />
      </Wrapper>
    )
  }

  return PickerWithState
}
